import { createBigDecimal, toStringBigDecimal } from '@0xtorch/big-decimal'
import { jpy } from '@0xtorch/core'
/**
 * 未 lock の action に対して ActionEditRule を優先順に適用し、rule 単位で DB へ保存する。
 * 候補 action と対象 transfer は一時テーブルへ固定し、大量更新でも SQL と I/O を抑えて処理する。
 */
import type { ActionFilter } from '@pkg/basic'
import { divideArrayIntoChunks } from '@pkg/basic'
import {
  type SQL,
  type SQLWrapper,
  and,
  desc,
  eq,
  inArray,
  isNotNull,
  isNull,
  ne,
  notInArray,
  or,
  sql,
} from 'drizzle-orm'
import { z } from 'zod'
import { sqliteMaxHostParameterCount } from '../../constants'
import {
  createJpyTradeAmount,
  findJpyTradeConvertibleTransfer,
} from '../../jpyTrade'
import {
  actionEditRuleTable,
  actionTable,
  actionTransferTable,
  cryptoCurrencyTable,
  nftTable,
  portfolioTable,
} from '../../schema'
import { andSqlConditions, orSqlConditions } from '../../sqlConditions'
import type {
  ActionEditRuleDraft,
  DatabaseWithTransaction,
  TransactionDatabase,
} from '../../types'
import { createWhereConditionsByActionFilterList } from '../../utils'
import { createActionTransferCountWhere } from '../../utils'
import { createActionEditRuleDraft } from '../read/createActionEditRuleDraft'
import {
  type ActionEditRuleAssetReference,
  parseActionEditRuleAssetReference,
} from './actionEditRuleAssetReference'
import { deleteAssistantProposalsForActionIds } from './deleteAssistantProposalsForActionKeys'

/** 一時テーブル: rule ごとの候補 action id を保持する。 */
const candidateActionTempTableName = '__actionEditRuleCandidateAction'

/** 一時テーブル: rule で実際に更新対象となる action id を保持する。 */
const affectedActionTempTableName = '__actionEditRuleAffectedAction'

/** 一時テーブル: rule 内の transfer 編集対象を固定する。 */
const transferTargetTempTableName = '__actionEditRuleTransferTarget'

/** transfer 編集対象の識別子。 */
const transferTargetTags = ['amount', 'asset', 'price'] as const

/** 集計用 count 行の検証 schema。 */
const countRowSchema = z.union([
  z.object({ value: z.number() }),
  z.tuple([z.number()]),
])

type TransferTargetTag = (typeof transferTargetTags)[number]

type ActionEditRuleDatabase = TransactionDatabase

type ApplyActionEditRulesParameters = {
  readonly database: DatabaseWithTransaction
  readonly actionIdList?: readonly string[]
  readonly fiatCurrencyIds: readonly string[]
  readonly ruleIdList?: readonly number[]
  readonly invalidateCalculationState?: boolean
}

type ApplyActionEditRulesOnDatabaseParameters = Omit<
  ApplyActionEditRulesParameters,
  'database'
> & {
  readonly database: ActionEditRuleDatabase
}

type ApplyActionEditRulesResult = {
  readonly appliedRuleCount: number
  readonly updatedActionCount: number
}

type RuleProcessingContext = {
  readonly actionIdChunks: readonly (readonly string[] | undefined)[]
  readonly draft: ActionEditRuleDraft
}

type SupportedActionEditRuleDraft = Pick<
  ActionEditRuleDraft,
  'conditions' | 'edits'
>

type TextCondition = NonNullable<
  SupportedActionEditRuleDraft['conditions']['comment']
>

type ArrayCondition<TValue extends string | number> = {
  readonly operator: 'in-array' | 'not-in-array'
  readonly value: readonly TValue[]
}

type MathCondition = NonNullable<
  SupportedActionEditRuleDraft['conditions']['transferIn']
>

type TargetedEdit = NonNullable<SupportedActionEditRuleDraft['edits']['amount']>

type ActionUpdateSet = {
  comment?: string
  crossId?: string
  crossType?: NonNullable<SupportedActionEditRuleDraft['edits']['crossType']>
  evidence?: NonNullable<SupportedActionEditRuleDraft['edits']['evidence']>
  generatedTx?: boolean
  loanId?: string
  locked?: boolean
  subType?: number | null
  type?: typeof actionTable.$inferInsert.type
}

/**
 * asset id に対応する transfer 列比較 SQL を返す。
 * @param params asset id を受け取る。
 * @returns 一致条件 SQL を返す。
 */
const createAssetReferenceMatchSql = ({
  assetReference,
}: {
  readonly assetReference: ActionEditRuleAssetReference
}): SQL<unknown> => {
  const matchCondition = or(
    eq(actionTransferTable.cryptoId, assetReference.assetId),
    eq(actionTransferTable.fiatId, assetReference.assetId),
    eq(actionTransferTable.nftId, assetReference.assetId),
  )

  if (matchCondition === undefined) {
    throw new Error('asset match condition should not be undefined')
  }

  return matchCondition
}

type ResolvedActionEditRuleAssetColumns = {
  readonly cryptoId: string | null
  readonly fiatId: string | null
  readonly nftId: string | null
}

/**
 * asset id を transfer の保存列へ解決する。
 * @param params database と asset id を受け取る。
 * @returns crypto/fiat/nft 各列へ設定する値を返す。
 */
const resolveActionEditRuleAssetColumns = async ({
  assetId,
  database,
  fiatCurrencyIds,
}: {
  readonly assetId: string
  readonly database: ActionEditRuleDatabase
  readonly fiatCurrencyIds: readonly string[]
}): Promise<ResolvedActionEditRuleAssetColumns> => {
  const [cryptoCurrencyRows, nftRows] = await Promise.all([
    database
      .select({ id: cryptoCurrencyTable.id })
      .from(cryptoCurrencyTable)
      .where(eq(cryptoCurrencyTable.id, assetId))
      .limit(1),
    database
      .select({ id: nftTable.id })
      .from(nftTable)
      .where(eq(nftTable.id, assetId))
      .limit(1),
  ])

  return {
    cryptoId: cryptoCurrencyRows[0]?.id ?? null,
    fiatId: fiatCurrencyIds.includes(assetId) ? assetId : null,
    nftId: nftRows[0]?.id ?? null,
  }
}

/**
 * asset 条件を action 候補抽出用の SQL where 句へ変換する。
 * @param params 条件を受け取る。
 * @returns 追加条件、または undefined を返す。
 */
const createAssetConditionWhere = ({
  condition,
}: {
  readonly condition?: SupportedActionEditRuleDraft['conditions']['asset']
}): SQL<unknown> | undefined => {
  if (condition === undefined || condition.value.length === 0) {
    return undefined
  }

  const referenceConditions = condition.value.map((value) =>
    createAssetReferenceMatchSql({
      assetReference: parseActionEditRuleAssetReference({ value }),
    }),
  )
  const combinedReferenceCondition = orSqlConditions({
    conditions: referenceConditions,
  })
  if (combinedReferenceCondition === undefined) {
    return undefined
  }

  if (condition.operator === 'in-array') {
    return sql`EXISTS (
      SELECT 1
      FROM ${actionTransferTable}
      WHERE ${actionTransferTable.actionId} = ${actionTable.id}
        AND ${combinedReferenceCondition}
    )`
  }

  return sql`NOT EXISTS (
    SELECT 1
    FROM ${actionTransferTable}
    WHERE ${actionTransferTable.actionId} = ${actionTable.id}
      AND ${combinedReferenceCondition}
  )`
}

/**
 * 文字列の配列条件を ActionFilter 用の inArray フラグへ変換する。
 * @param params 配列条件を受け取る。
 * @returns inArray 判定結果を返す。
 */
const toInArray = ({
  condition,
}: {
  readonly condition: ArrayCondition<string | number>
}): boolean => {
  return condition.operator === 'in-array'
}

/**
 * Text 条件の空文字未指定を filter 用の文字列へ正規化する。
 * @param params Text 条件を受け取る。
 * @returns 比較値を返す。
 */
const toFilterTextValue = ({
  condition,
}: {
  readonly condition: TextCondition
}): string => {
  return condition.value ?? ''
}

/**
 * rule のうち既存 ActionFilter へ落とせる条件を生成する。
 * @param params draft を受け取る。
 * @returns ActionFilter 配列を返す。
 */
const createActionFilterList = ({
  draft,
}: {
  readonly draft: ActionEditRuleDraft
}): ActionFilter[] => {
  const filterList: ActionFilter[] = []
  const { conditions } = draft

  if (conditions.app !== undefined) {
    filterList.push({
      type: 'app',
      appIdList: conditions.app.value,
      inArray: toInArray({ condition: conditions.app }),
      isOpenedMenu: false,
    })
  }
  if (conditions.amount !== undefined) {
    filterList.push({
      type: 'amount',
      operator: conditions.amount.operator,
      value: conditions.amount.value,
      isOpenedMenu: false,
    })
  }
  if (conditions.category !== undefined) {
    filterList.push({
      type: 'category',
      categoryIdList: conditions.category.value,
      inArray: toInArray({ condition: conditions.category }),
      isOpenedMenu: false,
    })
  }
  if (conditions.comment !== undefined) {
    filterList.push({
      type: 'comment',
      operator: conditions.comment.operator,
      value: toFilterTextValue({ condition: conditions.comment }),
      isOpenedMenu: false,
    })
  }
  if (conditions.crossId !== undefined) {
    filterList.push({
      type: 'cross-id',
      operator: conditions.crossId.operator,
      value: toFilterTextValue({ condition: conditions.crossId }),
      isOpenedMenu: false,
    })
  }
  if (conditions.evidence !== undefined) {
    filterList.push({
      type: 'evidence',
      evidenceList: conditions.evidence.value,
      inArray: toInArray({ condition: conditions.evidence }),
      isOpenedMenu: false,
    })
  }
  if (conditions.evmFunction !== undefined) {
    filterList.push({
      type: 'evm-function',
      operator: conditions.evmFunction.operator,
      value: toFilterTextValue({ condition: conditions.evmFunction }),
      isOpenedMenu: false,
    })
  }
  if (conditions.evmTo !== undefined) {
    filterList.push({
      type: 'evm-to',
      operator: conditions.evmTo.operator,
      value: toFilterTextValue({ condition: conditions.evmTo }),
      isOpenedMenu: false,
    })
  }
  if (conditions.loanId !== undefined) {
    filterList.push({
      type: 'loan-id',
      operator: conditions.loanId.operator,
      value: toFilterTextValue({ condition: conditions.loanId }),
      isOpenedMenu: false,
    })
  }
  if (conditions.transfer !== undefined) {
    filterList.push({
      type: 'transfer',
      operator: conditions.transfer.operator,
      addressList: conditions.transfer.value,
      isOpenedMenu: false,
    })
  }
  if (conditions.type !== undefined) {
    filterList.push({
      type: 'action',
      actionList: conditions.type.value,
      subTypeIdList: conditions.subType?.value ?? [],
      inArray: toInArray({ condition: conditions.type }),
      isOpenedMenu: false,
    })
  } else if (conditions.subType !== undefined) {
    filterList.push({
      type: 'action',
      actionList: [],
      subTypeIdList: conditions.subType.value,
      inArray: toInArray({ condition: conditions.subType }),
      isOpenedMenu: false,
    })
  }

  return filterList
}

/**
 * crossType 条件を SQL where 句へ変換する。
 * @param params 条件を受け取る。
 * @returns 追加条件、または undefined を返す。
 */
const createCrossTypeWhere = ({
  condition,
}: {
  readonly condition?: SupportedActionEditRuleDraft['conditions']['crossType']
}): SQL<unknown> | undefined => {
  if (condition === undefined || condition.value.length === 0) {
    return undefined
  }

  if (condition.value.length === 1) {
    if (condition.operator === 'in-array') {
      return eq(actionTable.crossType, condition.value[0])
    }
    return or(
      ne(actionTable.crossType, condition.value[0]),
      isNull(actionTable.crossType),
    )
  }

  if (condition.operator === 'in-array') {
    return inArray(actionTable.crossType, [...condition.value])
  }

  return or(
    notInArray(actionTable.crossType, [...condition.value]),
    isNull(actionTable.crossType),
  )
}

/**
 * transfer 数条件を SQL where 句へ変換する。
 * @param params direction と条件を受け取る。
 * @returns 追加条件、または undefined を返す。
 */
const createTransferCountWhere = ({
  direction,
  condition,
}: {
  readonly direction: 'in' | 'out'
  readonly condition?: MathCondition
}): SQL<unknown> | undefined => {
  if (condition === undefined) {
    return undefined
  }

  return createActionTransferCountWhere({
    actionIdSql: actionTable.id,
    direction,
    operator: condition.operator,
    value: condition.value,
  })
}

/**
 * actionId chunk 条件を SQL where 句へ変換する。
 * @param params actionId chunk を受け取る。
 * @returns 追加条件、または undefined を返す。
 */
const createActionIdChunkWhere = ({
  actionIdChunk,
}: {
  readonly actionIdChunk?: readonly string[]
}): SQL<unknown> | undefined => {
  if (actionIdChunk === undefined || actionIdChunk.length === 0) {
    return undefined
  }

  return inArray(actionTable.id, [...actionIdChunk])
}

/**
 * rule と actionId chunk から候補 action の where 句を構築する。
 * @param params draft と actionId chunk を受け取る。
 * @returns 候補 action の where 句を返す。
 */
const createCandidateWhere = ({
  draft,
  actionIdChunk,
}: {
  readonly draft: ActionEditRuleDraft
  readonly actionIdChunk?: readonly string[]
}): SQL<unknown> => {
  const whereConditions: SQL<unknown>[] = [eq(actionTable.locked, false)]
  whereConditions.push(
    ...createWhereConditionsByActionFilterList(
      createActionFilterList({ draft }),
    ),
  )

  const crossTypeWhere = createCrossTypeWhere({
    condition: draft.conditions.crossType,
  })
  if (crossTypeWhere !== undefined) {
    whereConditions.push(crossTypeWhere)
  }

  const assetWhere = createAssetConditionWhere({
    condition: draft.conditions.asset,
  })
  if (assetWhere !== undefined) {
    whereConditions.push(assetWhere)
  }

  const transferInWhere = createTransferCountWhere({
    direction: 'in',
    condition: draft.conditions.transferIn,
  })
  if (transferInWhere !== undefined) {
    whereConditions.push(transferInWhere)
  }

  const transferOutWhere = createTransferCountWhere({
    direction: 'out',
    condition: draft.conditions.transferOut,
  })
  if (transferOutWhere !== undefined) {
    whereConditions.push(transferOutWhere)
  }

  const actionIdChunkWhere = createActionIdChunkWhere({ actionIdChunk })
  if (actionIdChunkWhere !== undefined) {
    whereConditions.push(actionIdChunkWhere)
  }

  const combinedWhere = andSqlConditions({ conditions: whereConditions })
  if (combinedWhere === undefined) {
    throw new Error('candidate where should not be undefined')
  }

  return combinedWhere
}

/**
 * transfer 編集対象の絞り込み条件を構築する。
 * @param params targeted edit を受け取る。
 * @returns transfer 側 where 句、または undefined を返す。
 */
const createTransferTargetWhere = ({
  edit,
}: {
  readonly edit?: TargetedEdit
}): SQL<unknown> | undefined => {
  if (edit === undefined) {
    return undefined
  }

  const whereConditions: SQL<unknown>[] = []

  if (edit.targetAsset !== undefined) {
    whereConditions.push(
      createAssetReferenceMatchSql({
        assetReference: parseActionEditRuleAssetReference({
          value: edit.targetAsset,
        }),
      }),
    )
  }
  if (edit.targetDirection !== undefined) {
    whereConditions.push(
      eq(actionTransferTable.direction, edit.targetDirection),
    )
  }
  if (edit.targetPrice === 'is-empty') {
    whereConditions.push(isNull(actionTransferTable.price))
  }
  if (edit.targetPrice === 'is-not-empty') {
    whereConditions.push(isNotNull(actionTransferTable.price))
  }

  if (whereConditions.length === 0) {
    return undefined
  }

  return andSqlConditions({ conditions: whereConditions })
}

/**
 * テキスト条件が bind parameter を何個使うかを返す。
 * @param params text 条件を受け取る。
 * @returns bind parameter 数を返す。
 */
const countTextConditionBindParameters = ({
  condition,
}: {
  readonly condition?: TextCondition
}): number => {
  if (condition === undefined) {
    return 0
  }

  switch (condition.operator) {
    case 'eq':
    case 'like':
    case 'not-like': {
      return condition.value === undefined || condition.value.length === 0
        ? 0
        : 1
    }
    case 'is-empty':
    case 'is-not-empty': {
      return 0
    }
    default: {
      throw new Error(`Unknown text operator: ${condition.operator}`)
    }
  }
}

/**
 * 配列条件が bind parameter を何個使うかを返す。
 * @param params 配列条件を受け取る。
 * @returns bind parameter 数を返す。
 */
const countArrayConditionBindParameters = <TValue extends string | number>({
  condition,
}: {
  readonly condition?: ArrayCondition<TValue>
}): number => {
  if (condition === undefined) {
    return 0
  }

  return condition.value.length
}

/**
 * transfer 条件が bind parameter を何個使うかを返す。
 * @param params transfer 条件を受け取る。
 * @returns bind parameter 数を返す。
 */
const countTransferConditionBindParameters = ({
  condition,
}: {
  readonly condition?: SupportedActionEditRuleDraft['conditions']['transfer']
}): number => {
  if (condition === undefined) {
    return 0
  }

  switch (condition.operator) {
    case 'in-array':
    case 'not-in-array': {
      return condition.value.length * 2
    }
    case 'in-array-from':
    case 'in-array-to':
    case 'not-in-array-from':
    case 'not-in-array-to': {
      return condition.value.length
    }
    default: {
      throw new Error(`Unknown transfer operator: ${condition.operator}`)
    }
  }
}

/**
 * transfer 数条件が bind parameter を何個使うかを返す。
 * @param params 数条件を受け取る。
 * @returns bind parameter 数を返す。
 */
const countTransferCountBindParameters = ({
  condition,
}: {
  readonly condition?: MathCondition
}): number => {
  return condition === undefined ? 0 : 2
}

/**
 * rule の候補抽出が actionId 以外で消費する bind parameter 数を見積もる。
 * @param params draft を受け取る。
 * @returns bind parameter 数の上限見積もりを返す。
 */
const estimateRuleBindParameterCount = ({
  draft,
}: {
  readonly draft: ActionEditRuleDraft
}): number => {
  return (
    1 +
    countArrayConditionBindParameters({ condition: draft.conditions.app }) +
    countArrayConditionBindParameters({
      condition: draft.conditions.category,
    }) +
    countTextConditionBindParameters({ condition: draft.conditions.comment }) +
    countTextConditionBindParameters({ condition: draft.conditions.crossId }) +
    countArrayConditionBindParameters({
      condition: draft.conditions.crossType,
    }) +
    countArrayConditionBindParameters({
      condition: draft.conditions.evidence,
    }) +
    countTextConditionBindParameters({
      condition: draft.conditions.evmFunction,
    }) +
    countTextConditionBindParameters({ condition: draft.conditions.evmTo }) +
    countTextConditionBindParameters({ condition: draft.conditions.loanId }) +
    countTransferConditionBindParameters({
      condition: draft.conditions.transfer,
    }) +
    countTransferCountBindParameters({
      condition: draft.conditions.transferIn,
    }) +
    countTransferCountBindParameters({
      condition: draft.conditions.transferOut,
    }) +
    countArrayConditionBindParameters({ condition: draft.conditions.type }) +
    countArrayConditionBindParameters({ condition: draft.conditions.subType }) +
    countArrayConditionBindParameters({ condition: draft.conditions.asset })
  )
}

/**
 * action row 自体の更新内容を抽出する。
 * @param params draft を受け取る。
 * @returns actionTable 用の更新内容を返す。
 */
const createActionUpdateSet = ({
  draft,
}: {
  readonly draft: ActionEditRuleDraft
}): ActionUpdateSet => {
  const set: ActionUpdateSet = {}
  const { edits } = draft

  if (edits.comment !== undefined) {
    set.comment = edits.comment
  }
  if (edits.crossId !== undefined) {
    set.crossId = edits.crossId
  }
  if (edits.crossType !== undefined) {
    set.crossType = edits.crossType
  }
  if (edits.evidence !== undefined) {
    set.evidence = edits.evidence
  }
  if (edits.loanId !== undefined) {
    set.loanId = edits.loanId
  }
  if (edits.locked !== undefined) {
    set.locked = edits.locked
  }
  if (edits.type !== undefined && edits.type !== 'convert-to-jpy-trade') {
    set.type = edits.type
    set.subType = edits.subType ?? null
  } else if (edits.subType !== undefined) {
    set.subType = edits.subType
  }
  if (Object.keys(set).length > 0) {
    set.generatedTx = false
  }

  return set
}

/**
 * action row 更新の有無を返す。
 * @param params update set を受け取る。
 * @returns action row 更新があれば true を返す。
 */
const hasActionUpdates = ({
  updateSet,
}: {
  readonly updateSet: ActionUpdateSet
}): boolean => {
  return Object.keys(updateSet).length > 0
}

/**
 * transfer 編集の tag 一覧を返す。
 * @param params draft を受け取る。
 * @returns 対象 tag の一覧を返す。
 */
const getTransferTargetTags = ({
  draft,
}: {
  readonly draft: ActionEditRuleDraft
}): TransferTargetTag[] => {
  const tags: TransferTargetTag[] = []
  if (draft.edits.amount !== undefined) {
    tags.push('amount')
  }
  if (draft.edits.asset !== undefined) {
    tags.push('asset')
  }
  if (draft.edits.price !== undefined) {
    tags.push('price')
  }
  return tags
}

/**
 * 指定 rule に対する transfer tag の対象条件を返す。
 * @param params draft と tag を受け取る。
 * @returns transfer 側 where 句、または undefined を返す。
 */
const createTransferTargetWhereByTag = ({
  draft,
  tag,
}: {
  readonly draft: ActionEditRuleDraft
  readonly tag: TransferTargetTag
}): SQL<unknown> | undefined => {
  switch (tag) {
    case 'amount': {
      return createTransferTargetWhere({ edit: draft.edits.amount })
    }
    case 'asset': {
      return createTransferTargetWhere({ edit: draft.edits.asset })
    }
    case 'price': {
      return createTransferTargetWhere({ edit: draft.edits.price })
    }
    default: {
      throw new Error(`Unknown transfer target tag: ${tag satisfies never}`)
    }
  }
}

/**
 * temp table 名を SQL へ埋め込む raw fragment を返す。
 * @param params temp table 名を受け取る。
 * @returns SQL raw fragment を返す。
 */
const createTempTableSql = ({
  tableName,
}: {
  readonly tableName: string
}): SQL<unknown> => {
  return sql.raw(`"${tableName}"`)
}

/**
 * count クエリの戻り値から数値を取り出す。
 * @param params rows を受け取る。
 * @returns count 値を返す。
 */
const readCountValue = ({
  rows,
}: {
  readonly rows: readonly (readonly [number] | { readonly value: number })[]
}): number => {
  const row = rows[0]
  if (row === undefined) {
    return 0
  }

  const parsedRow = countRowSchema.safeParse(row)
  if (!parsedRow.success) {
    return 0
  }

  return Array.isArray(parsedRow.data)
    ? parsedRow.data[0]
    : parsedRow.data.value
}

/**
 * temp table の件数を取得する。
 * @param params database と temp table 名を受け取る。
 * @returns 行数を返す。
 */
const countTempTableRows = async ({
  database,
  tableName,
}: {
  readonly database: ActionEditRuleDatabase
  readonly tableName: string
}): Promise<number> => {
  const rows = await database.all<
    { readonly value: number } | readonly [number]
  >(sql`
    SELECT COUNT(*) AS value
    FROM ${createTempTableSql({ tableName })}
  `)

  return readCountValue({ rows })
}

/**
 * rule 適用用の一時テーブル群を作成する。
 * @param params database を受け取る。
 * @returns 値は返さない。
 */
const createTempTables = async ({
  database,
}: {
  readonly database: ActionEditRuleDatabase
}): Promise<void> => {
  await database.run(
    `CREATE TEMP TABLE IF NOT EXISTS "${candidateActionTempTableName}" ("id" TEXT PRIMARY KEY NOT NULL)`,
  )
  await database.run(
    `CREATE TEMP TABLE IF NOT EXISTS "${affectedActionTempTableName}" ("id" TEXT PRIMARY KEY NOT NULL)`,
  )
  await database.run(
    `CREATE TEMP TABLE IF NOT EXISTS "${transferTargetTempTableName}" ("tag" TEXT NOT NULL, "actionId" TEXT NOT NULL, "order" INTEGER NOT NULL, PRIMARY KEY ("tag", "actionId", "order"))`,
  )
}

/**
 * rule 適用前に一時テーブル群を空にする。
 * @param params database を受け取る。
 * @returns 値は返さない。
 */
const clearTempTables = async ({
  database,
}: {
  readonly database: ActionEditRuleDatabase
}): Promise<void> => {
  await database.run(`DELETE FROM "${candidateActionTempTableName}"`)
  await database.run(`DELETE FROM "${affectedActionTempTableName}"`)
  await database.run(`DELETE FROM "${transferTargetTempTableName}"`)
}

/**
 * rule 適用終了後に一時テーブル群を削除する。
 * @param params database を受け取る。
 * @returns 値は返さない。
 */
const dropTempTables = async ({
  database,
}: {
  readonly database: ActionEditRuleDatabase
}): Promise<void> => {
  await database.run(`DROP TABLE IF EXISTS "${transferTargetTempTableName}"`)
  await database.run(`DROP TABLE IF EXISTS "${affectedActionTempTableName}"`)
  await database.run(`DROP TABLE IF EXISTS "${candidateActionTempTableName}"`)
}

/**
 * 対象 action id の chunk 配列を返す。
 * @param params actionIdList を受け取る。
 * @returns chunk 配列を返す。
 */
const createActionIdChunks = ({
  draft,
  actionIdList,
}: {
  readonly draft: ActionEditRuleDraft
  readonly actionIdList?: readonly string[]
}): readonly (readonly string[] | undefined)[] => {
  const reservedBindParameterCount = estimateRuleBindParameterCount({ draft })
  if (reservedBindParameterCount >= sqliteMaxHostParameterCount) {
    throw new Error(
      `Rule uses too many SQL parameters: ${reservedBindParameterCount}`,
    )
  }

  if (actionIdList === undefined || actionIdList.length === 0) {
    return [undefined]
  }

  return divideArrayIntoChunks(
    actionIdList,
    Math.max(sqliteMaxHostParameterCount - reservedBindParameterCount, 1),
  )
}

/**
 * 集約列用に、transfer 順の先頭出現だけを残した文字列連結 SQL を返す。
 * @param params 集約対象の値 SQL を受け取る。
 * @returns 重複除去済み group_concat SQL を返す。
 */
const createDistinctOrderedGroupConcatSql = ({
  valueSql,
}: {
  readonly valueSql: SQLWrapper
}): SQL<unknown> => {
  return sql`COALESCE((
    SELECT group_concat("value")
    FROM (
      SELECT "value"
      FROM (
        SELECT ${valueSql} AS "value", ${actionTransferTable.order} AS "transferOrder"
        FROM ${actionTransferTable}
        WHERE ${actionTransferTable.actionId} = ${actionTable.id}
      )
      WHERE "value" IS NOT NULL
      GROUP BY "value"
      ORDER BY MIN("transferOrder")
    )
  ), '')`
}

/**
 * rule 一覧を優先順で読み込む。
 * @param params database と ruleIdList を受け取る。
 * @returns draft 一覧を返す。
 */
const loadRuleDrafts = async ({
  database,
  ruleIdList,
}: {
  readonly database: ActionEditRuleDatabase
  readonly ruleIdList?: readonly number[]
}): Promise<readonly ActionEditRuleDraft[]> => {
  if (ruleIdList === undefined || ruleIdList.length === 0) {
    const rows = await database
      .select()
      .from(actionEditRuleTable)
      .where(
        or(
          eq(actionEditRuleTable.stopped, false),
          isNull(actionEditRuleTable.stopped),
        ),
      )
      .orderBy(desc(actionEditRuleTable.priority), desc(actionEditRuleTable.id))

    return rows.map((row) => createActionEditRuleDraft({ row }))
  }

  const rows = []
  for (const ruleIdChunk of divideArrayIntoChunks(
    ruleIdList,
    sqliteMaxHostParameterCount,
  )) {
    const chunkRows = await database
      .select()
      .from(actionEditRuleTable)
      .where(
        and(
          inArray(actionEditRuleTable.id, [...ruleIdChunk]),
          or(
            eq(actionEditRuleTable.stopped, false),
            isNull(actionEditRuleTable.stopped),
          ),
        ),
      )

    rows.push(...chunkRows)
  }

  rows.sort((left, right) => {
    if (left.priority !== right.priority) {
      return right.priority - left.priority
    }
    return right.id - left.id
  })

  return rows.map((row) => createActionEditRuleDraft({ row }))
}

/**
 * 候補 action 一時テーブルへ rule の対象 action を積む。
 * @param params database と rule context を受け取る。
 * @returns 値は返さない。
 */
const fillCandidateActionTempTable = async ({
  database,
  context,
}: {
  readonly database: ActionEditRuleDatabase
  readonly context: RuleProcessingContext
}): Promise<void> => {
  for (const actionIdChunk of context.actionIdChunks) {
    const candidateWhere = createCandidateWhere({
      draft: context.draft,
      actionIdChunk,
    })

    await database.run(sql`
      INSERT OR IGNORE INTO ${createTempTableSql({ tableName: candidateActionTempTableName })} ("id")
      SELECT ${actionTable.id}
      FROM ${actionTable}
      WHERE ${candidateWhere}
    `)
  }
}

/**
 * 指定 tag の transfer 対象を一時テーブルへ固定する。
 * @param params database と draft、tag を受け取る。
 * @returns 値は返さない。
 */
const fillTransferTargetTempTable = async ({
  database,
  draft,
  tag,
}: {
  readonly database: ActionEditRuleDatabase
  readonly draft: ActionEditRuleDraft
  readonly tag: TransferTargetTag
}): Promise<void> => {
  const targetWhere = createTransferTargetWhereByTag({ draft, tag })
  const candidateActionWhere = sql`${actionTransferTable.actionId} IN (
    SELECT "id" FROM ${createTempTableSql({ tableName: candidateActionTempTableName })}
  )`

  const where =
    targetWhere === undefined
      ? candidateActionWhere
      : and(candidateActionWhere, targetWhere)

  if (where === undefined) {
    return
  }

  await database.run(sql`
    INSERT OR IGNORE INTO ${createTempTableSql({ tableName: transferTargetTempTableName })} ("tag", "actionId", "order")
    SELECT ${tag}, ${actionTransferTable.actionId}, ${actionTransferTable.order}
    FROM ${actionTransferTable}
    WHERE ${where}
  `)
}

/**
 * 影響を受ける action 一時テーブルを構築する。
 * @param params database と draft、actionUpdateSet を受け取る。
 * @returns 値は返さない。
 */
const fillAffectedActionTempTable = async ({
  database,
  draft,
  actionUpdateSet,
}: {
  readonly database: ActionEditRuleDatabase
  readonly draft: ActionEditRuleDraft
  readonly actionUpdateSet: ActionUpdateSet
}): Promise<void> => {
  if (hasActionUpdates({ updateSet: actionUpdateSet })) {
    await database.run(sql`
      INSERT OR IGNORE INTO ${createTempTableSql({ tableName: affectedActionTempTableName })} ("id")
      SELECT "id"
      FROM ${createTempTableSql({ tableName: candidateActionTempTableName })}
    `)
  }

  if (draft.edits.type === 'convert-to-jpy-trade') {
    await database.run(sql`
      INSERT OR IGNORE INTO ${createTempTableSql({ tableName: affectedActionTempTableName })} ("id")
      SELECT ${actionTransferTable.actionId}
      FROM ${actionTransferTable}
      WHERE ${actionTransferTable.actionId} IN (
        SELECT "id" FROM ${createTempTableSql({ tableName: candidateActionTempTableName })}
      )
      GROUP BY ${actionTransferTable.actionId}
      HAVING COUNT(*) = 1 AND MAX(${actionTransferTable.direction}) IN ('in', 'out')
    `)
  }

  for (const tag of getTransferTargetTags({ draft })) {
    await fillTransferTargetTempTable({ database, draft, tag })
  }

  if (getTransferTargetTags({ draft }).length > 0) {
    await database.run(sql`
      INSERT OR IGNORE INTO ${createTempTableSql({ tableName: affectedActionTempTableName })} ("id")
      SELECT DISTINCT "actionId"
      FROM ${createTempTableSql({ tableName: transferTargetTempTableName })}
    `)
  }
}

/**
 * action row 更新を実行する。
 * @param params database と更新内容を受け取る。
 * @returns 値は返さない。
 */
const updateActionRows = async ({
  database,
  updateSet,
}: {
  readonly database: ActionEditRuleDatabase
  readonly updateSet: ActionUpdateSet
}): Promise<void> => {
  if (!hasActionUpdates({ updateSet })) {
    return
  }

  await database
    .update(actionTable)
    .set(updateSet)
    .where(sql`${actionTable.id} IN (
      SELECT "id"
      FROM ${createTempTableSql({ tableName: candidateActionTempTableName })}
    )`)
}

/**
 * amount 編集を実行する。
 * @param params database と draft を受け取る。
 * @returns 値は返さない。
 */
const updateTransferAmounts = async ({
  database,
  draft,
}: {
  readonly database: ActionEditRuleDatabase
  readonly draft: ActionEditRuleDraft
}): Promise<void> => {
  if (draft.edits.amount === undefined) {
    return
  }

  await database
    .update(actionTransferTable)
    .set({ amount: draft.edits.amount.value })
    .where(sql`EXISTS (
      SELECT 1
      FROM ${createTempTableSql({ tableName: transferTargetTempTableName })}
      WHERE "tag" = 'amount'
        AND "actionId" = ${actionTransferTable.actionId}
        AND "order" = ${actionTransferTable.order}
    )`)
}

/**
 * asset 編集を実行する。
 * @param params database と draft を受け取る。
 * @returns 値は返さない。
 */
const updateTransferAssets = async ({
  database,
  draft,
  fiatCurrencyIds,
}: {
  readonly database: ActionEditRuleDatabase
  readonly draft: ActionEditRuleDraft
  readonly fiatCurrencyIds: readonly string[]
}): Promise<void> => {
  if (draft.edits.asset === undefined) {
    return
  }

  const assetReference = parseActionEditRuleAssetReference({
    value: draft.edits.asset.value,
  })
  const { cryptoId, fiatId, nftId } = await resolveActionEditRuleAssetColumns({
    assetId: assetReference.assetId,
    database,
    fiatCurrencyIds,
  })

  await database
    .update(actionTransferTable)
    .set({
      cryptoId,
      fiatId,
      nftId,
    })
    .where(sql`EXISTS (
      SELECT 1
      FROM ${createTempTableSql({ tableName: transferTargetTempTableName })}
      WHERE "tag" = 'asset'
        AND "actionId" = ${actionTransferTable.actionId}
        AND "order" = ${actionTransferTable.order}
    )`)
}

/**
 * price 編集を実行する。
 * @param params database と draft を受け取る。
 * @returns 値は返さない。
 */
const updateTransferPrices = async ({
  database,
  draft,
}: {
  readonly database: ActionEditRuleDatabase
  readonly draft: ActionEditRuleDraft
}): Promise<void> => {
  if (draft.edits.price === undefined) {
    return
  }

  await database
    .update(actionTransferTable)
    .set({ price: draft.edits.price.value })
    .where(sql`EXISTS (
      SELECT 1
      FROM ${createTempTableSql({ tableName: transferTargetTempTableName })}
      WHERE "tag" = 'price'
        AND "actionId" = ${actionTransferTable.actionId}
        AND "order" = ${actionTransferTable.order}
    )`)
}

/**
 * 候補取引に円の資産移動を追加し、売買取引へ変換する。
 * @param params database と適用するルール。
 * @returns 値は返さない。変換できない取引はスキップする。
 */
const convertCandidateActionsToJpyTrade = async ({
  database,
  draft,
}: {
  readonly database: ActionEditRuleDatabase
  readonly draft: ActionEditRuleDraft
}): Promise<void> => {
  if (draft.edits.type !== 'convert-to-jpy-trade') return
  const transfers = await database
    .select()
    .from(actionTransferTable)
    .where(sql`
    ${actionTransferTable.actionId} IN (
      SELECT "id" FROM ${createTempTableSql({ tableName: candidateActionTempTableName })}
    )
  `)
  const grouped = new Map<string, (typeof transfers)[number][]>()
  for (const transfer of transfers) {
    const group = grouped.get(transfer.actionId) ?? []
    group.push(transfer)
    grouped.set(transfer.actionId, group)
  }
  for (const [actionId, group] of grouped) {
    const source = findJpyTradeConvertibleTransfer({ transfers: group })
    if (source === undefined) continue
    await database.insert(actionTransferTable).values({
      actionId,
      order: source.order + 1,
      direction: source.direction === 'in' ? 'out' : 'in',
      fiatId: jpy.id,
      amount: toStringBigDecimal(
        createJpyTradeAmount({
          amount: createBigDecimal(source.amount),
          price:
            source.price === null ? undefined : createBigDecimal(source.price),
        }),
      ),
      price: '1',
    })
    await database
      .update(actionTable)
      .set({
        type: 'trade',
        subType: null,
        loanId: draft.edits.loanId ?? null,
        crossId: draft.edits.crossId ?? null,
        crossType: draft.edits.crossType ?? null,
        evidence: draft.edits.evidence ?? 'user-rule',
        locked: draft.edits.locked ?? true,
        generatedTx: false,
      })
      .where(eq(actionTable.id, actionId))
  }
}

/**
 * transfer 編集後の action 集約列を再計算する。
 * @param params database を受け取る。
 * @returns 値は返さない。
 */
const refreshActionTransferCaches = async ({
  database,
}: {
  readonly database: ActionEditRuleDatabase
}): Promise<void> => {
  await database
    .update(actionTable)
    .set({
      generatedTx: false,
      relatedAssetIds: createDistinctOrderedGroupConcatSql({
        valueSql: sql`CASE
          WHEN ${actionTransferTable.cryptoId} IS NOT NULL THEN ${actionTransferTable.cryptoId}
          WHEN ${actionTransferTable.fiatId} IS NOT NULL THEN ${actionTransferTable.fiatId}
          ELSE ${actionTransferTable.nftId}
        END`,
      }),
      fromAddresses: createDistinctOrderedGroupConcatSql({
        valueSql: actionTransferTable.from,
      }),
      toAddresses: createDistinctOrderedGroupConcatSql({
        valueSql: actionTransferTable.to,
      }),
    })
    .where(sql`${actionTable.id} IN (
      SELECT "id"
      FROM ${createTempTableSql({ tableName: affectedActionTempTableName })}
    )`)
}

/**
 * portfolio の transaction 生成済み状態を無効化する。
 * @param params database を受け取る。
 * @returns 値は返さない。
 */
const markPortfolioTransactionAsDirty = async ({
  database,
}: {
  readonly database: ActionEditRuleDatabase
}): Promise<void> => {
  await database.update(portfolioTable).set({ isTxGenerated: false })
}

/**
 * rule 1 件を適用し、更新 action 件数を返す。
 * @param params database と draft、actionIdChunks を受け取る。
 * @returns 更新 action 件数を返す。
 */
const applyRule = async ({
  database,
  context,
  fiatCurrencyIds,
  invalidateCalculationState,
}: {
  readonly database: ActionEditRuleDatabase
  readonly context: RuleProcessingContext
  readonly fiatCurrencyIds: readonly string[]
  readonly invalidateCalculationState: boolean
}): Promise<number> => {
  await clearTempTables({ database })
  await fillCandidateActionTempTable({ database, context })

  const candidateCount = await countTempTableRows({
    database,
    tableName: candidateActionTempTableName,
  })
  if (candidateCount === 0) {
    return 0
  }

  const actionUpdateSet = createActionUpdateSet({ draft: context.draft })
  await fillAffectedActionTempTable({
    database,
    draft: context.draft,
    actionUpdateSet,
  })

  const affectedCount = await countTempTableRows({
    database,
    tableName: affectedActionTempTableName,
  })
  if (affectedCount === 0) {
    return 0
  }

  const affectedActionRows = await database
    .select({ id: actionTable.id })
    .from(actionTable)
    .where(sql`${actionTable.id} IN (
      SELECT "id"
      FROM ${createTempTableSql({ tableName: affectedActionTempTableName })}
    )`)
  await deleteAssistantProposalsForActionIds({
    database,
    actionIds: affectedActionRows.map((row) => row.id),
  })
  await updateActionRows({ database, updateSet: actionUpdateSet })
  await updateTransferAmounts({ database, draft: context.draft })
  await updateTransferAssets({
    database,
    draft: context.draft,
    fiatCurrencyIds,
  })
  await updateTransferPrices({ database, draft: context.draft })

  await convertCandidateActionsToJpyTrade({ database, draft: context.draft })

  if (
    getTransferTargetTags({ draft: context.draft }).length > 0 ||
    context.draft.edits.type === 'convert-to-jpy-trade'
  ) {
    await refreshActionTransferCaches({ database })
  }

  if (invalidateCalculationState) {
    await markPortfolioTransactionAsDirty({ database })
  }
  return affectedCount
}

/**
 * 指定 database 上で未 lock の action に ActionEditRule を優先順で適用する。
 * @param params database と対象 action id / rule id の任意フィルタを受け取る。
 * @returns 適用された rule 件数と更新 action 件数を返す。
 */
export const applyActionEditRulesOnDatabase = async ({
  database,
  actionIdList,
  fiatCurrencyIds,
  ruleIdList,
  invalidateCalculationState = true,
}: ApplyActionEditRulesOnDatabaseParameters): Promise<ApplyActionEditRulesResult> => {
  await createTempTables({ database })

  try {
    const drafts = await loadRuleDrafts({ database, ruleIdList })
    let appliedRuleCount = 0
    let updatedActionCount = 0

    for (const draft of drafts) {
      const actionIdChunks = createActionIdChunks({ actionIdList, draft })
      const updatedCount = await applyRule({
        database,
        context: {
          actionIdChunks,
          draft,
        },
        fiatCurrencyIds,
        invalidateCalculationState,
      })

      if (updatedCount === 0) {
        continue
      }

      appliedRuleCount += 1
      updatedActionCount += updatedCount
    }

    return {
      appliedRuleCount,
      updatedActionCount,
    }
  } finally {
    await dropTempTables({ database })
  }
}

/**
 * 未 lock の action に ActionEditRule を優先順で適用する。
 * @param params database と対象 action id / rule id の任意フィルタを受け取る。
 * @returns 適用された rule 件数と更新 action 件数を返す。
 */
export const applyActionEditRules = async ({
  database,
  actionIdList,
  fiatCurrencyIds,
  ruleIdList,
  invalidateCalculationState = true,
}: ApplyActionEditRulesParameters): Promise<ApplyActionEditRulesResult> => {
  return applyActionEditRulesOnDatabase({
    database: database.database,
    actionIdList,
    fiatCurrencyIds,
    ruleIdList,
    invalidateCalculationState,
  })
}
